import React, { useEffect, useState } from 'react';
import Tour from 'reactour';

export interface TourStep {
  selector: string;
  content: string;
}

interface TourProviderProps {
  tourKey: string;
  steps: TourStep[];
  toursSeen: string[];
  markTourAsSeenUrl: string;
}

const TourProvider: React.FC<TourProviderProps> = ({ tourKey, steps, toursSeen, markTourAsSeenUrl }) => {
  // Check if restart_tour parameter is in URL with matching key
  const urlParams = new URLSearchParams(window.location.search);
  const restartTour = urlParams.get('restart_tour') === tourKey;
  
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [validSteps, setValidSteps] = useState<TourStep[]>([]);

  // Listen for custom event to restart tour
  useEffect(() => {
    const handleRestartTour = (event: CustomEvent) => {
      if (event.detail.tourKey === tourKey && validSteps.length > 0) {
        setIsTourOpen(true);
      }
    };

    window.addEventListener('restartTour', handleRestartTour as EventListener);
    return () => window.removeEventListener('restartTour', handleRestartTour as EventListener);
  }, [tourKey, validSteps]);

  // Force opacity on mask after tour opens and fix Finish button
  useEffect(() => {
    if (isTourOpen) {
      const applyMaskOpacity = () => {
        // Find the reactour mask SVG and force opacity
        const maskDiv = document.querySelector('div[id*="reactour"]');
        if (maskDiv) {
          const svg = maskDiv.querySelector('svg');
          if (svg) {
            (svg as HTMLElement).style.opacity = '0.7';
            const rects = svg.querySelectorAll('rect');
            rects.forEach((rect) => {
              (rect as SVGRectElement).style.fillOpacity = '0.7';
              (rect as SVGRectElement).style.opacity = '0.7';
            });
          }
        }
      };

      const fixFinishButton = () => {
        // Get theme color from CSS variable
        const root = document.documentElement;
        const themeColor = getComputedStyle(root).getPropertyValue('--tour-primary-color').trim() || '#e0003c';
        
        // Find the controls area which contains the Next/Finish button
        const controls = document.querySelector('[data-tour-elem="controls"]');
        if (controls) {
          const buttons = controls.querySelectorAll('button');
          buttons.forEach((btn) => {
            const btnEl = btn as HTMLElement;
            // Skip arrow buttons
            if (btnEl.getAttribute('data-tour-elem')?.includes('arrow')) return;
            // Skip dot buttons
            if (btnEl.getAttribute('data-tour-elem') === 'dot') return;
            
            // This should be the Next/Finish button
            const text = btnEl.textContent?.trim();
            if (text === 'Finish' || text === 'Next' || text?.includes('Finish')) {
              // Force rectangular button styles
              btnEl.style.setProperty('padding', '10px 24px', 'important');
              btnEl.style.setProperty('border-radius', '4px', 'important');
              btnEl.style.setProperty('font-size', '15px', 'important');
              btnEl.style.setProperty('font-weight', '500', 'important');
              btnEl.style.setProperty('background-color', themeColor, 'important');
              btnEl.style.setProperty('color', 'white', 'important');
              btnEl.style.setProperty('border', 'none', 'important');
              btnEl.style.setProperty('cursor', 'pointer', 'important');
              btnEl.style.setProperty('box-shadow', '0 1px 3px rgba(0, 0, 0, 0.12)', 'important');
              btnEl.style.setProperty('width', 'auto', 'important');
              btnEl.style.setProperty('height', 'auto', 'important');
              btnEl.style.setProperty('min-width', 'auto', 'important');
              btnEl.style.setProperty('min-height', 'auto', 'important');
            }
          });
        }
        
        // Also check the navigation area (first div) for any Finish buttons
        const helper = document.querySelector('[data-tour-elem="helper"]');
        if (helper) {
          const navArea = helper.querySelector('> div:first-child');
          if (navArea) {
            const buttons = navArea.querySelectorAll('button');
            buttons.forEach((btn) => {
              const btnEl = btn as HTMLElement;
              const text = btnEl.textContent?.trim();
              if (text === 'Finish' || text?.includes('Finish')) {
                // Skip if it's an arrow
                if (btnEl.getAttribute('data-tour-elem')?.includes('arrow')) return;
                
                // Style it
                btnEl.style.setProperty('padding', '10px 24px', 'important');
                btnEl.style.setProperty('border-radius', '4px', 'important');
                btnEl.style.setProperty('width', 'auto', 'important');
                btnEl.style.setProperty('height', 'auto', 'important');
                btnEl.style.setProperty('min-width', 'auto', 'important');
                btnEl.style.setProperty('min-height', 'auto', 'important');
              }
            });
          }
        }
      };

      // Apply immediately and also after delays
      applyMaskOpacity();
      fixFinishButton();
      const timer1 = setTimeout(() => {
        applyMaskOpacity();
        fixFinishButton();
      }, 100);
      const timer2 = setTimeout(() => {
        applyMaskOpacity();
        fixFinishButton();
      }, 500);
      
      // Also observe for changes
      const observer = new MutationObserver(() => {
        applyMaskOpacity();
        fixFinishButton();
      });
      const maskDiv = document.querySelector('div[id*="reactour"]');
      if (maskDiv) {
        observer.observe(maskDiv, { childList: true, subtree: true, attributes: true });
      }

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        observer.disconnect();
      };
    }
  }, [isTourOpen]);

  useEffect(() => {
    // Wait for DOM to be ready and check which steps have valid selectors
    const checkStepsAndStart = () => {
      const existingSteps = steps.filter(step => {
        try {
          const element = document.querySelector(step.selector);
          return !!element;
        } catch (e) {
          return false;
        }
      });

      setValidSteps(existingSteps);

      // Check if this specific tour has been seen
      const thisTourSeen = toursSeen.includes(tourKey);

      // Only open tour if we have valid steps and should show it
      if (existingSteps.length > 0 && (!thisTourSeen || restartTour)) {
        setIsTourOpen(true);
      }

      if (restartTour) {
        // Clean up URL parameter after tour starts
        const url = new URL(window.location.href);
        url.searchParams.delete('restart_tour');
        window.history.replaceState({}, '', url.toString());
      }
    };

    // Wait for the page and React components to be ready
    const timer = setTimeout(checkStepsAndStart, 1000);
    return () => clearTimeout(timer);
  }, [steps, toursSeen, restartTour, tourKey]);

  const handleTourClose = async () => {
    setIsTourOpen(false);

    // Only mark this specific tour as seen if it hasn't been seen before
    const thisTourSeen = toursSeen.includes(tourKey);
    if (!thisTourSeen) {
      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        await fetch(markTourAsSeenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
          },
          body: JSON.stringify({ tour_key: tourKey }),
        });
      } catch (error) {
        // Silently fail
      }
    }
  };

  if (validSteps.length === 0) {
    return null;
  }

  // Get theme color from CSS variable
  const getThemeColor = () => {
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      const color = getComputedStyle(root).getPropertyValue('--tour-primary-color').trim();
      return color || '#e0003c'; // Fallback to default brand-primary
    }
    return '#e0003c';
  };

      return (
        <Tour
          steps={validSteps}
          isOpen={isTourOpen}
          onRequestClose={handleTourClose}
          accentColor={getThemeColor()}
          rounded={5}
          showNumber={false}
          showNavigation={true}
          showNavigationNumber={false}
          showButtons={true}
          closeWithMask={false}
          disableInteraction={false}
          disableKeyboardNavigation={false}
          maskClassName="tour-mask"
          highlightedMaskClassName="tour-highlighted"
          maskSpace={10}
          inViewThreshold={100}
          scrollDuration={300}
          styles={{
            maskArea: (base: any) => ({ ...base, rx: 5 }),
            maskWrapper: (base: any) => ({ ...base, opacity: 0.3 }),
            svgWrapper: (base: any) => ({ ...base, opacity: 0.3 }),
          }}
        />
      );
};

export default TourProvider;

